import { create } from "zustand";

/**
 * Which books / entities / accounts the positions dashboard loads. Empty lists
 * mean "every readable row" on that axis; the server intersects with the
 * user's grants (GET /positions/snapshot, /positions/summary and the WS
 * positions stream all take the same scope).
 */
export interface PositionsScope {
  books: string[];
  entities: string[];
  accounts: string[];
}

export const ALL_POSITIONS_SCOPE: PositionsScope = { books: [], entities: [], accounts: [] };

const RECENT_KEY = "atlas.positions.scope.recent";
const DEFAULT_CACHE_KEY = "atlas.positions.scope.default";
const MAX_RECENT = 5;

function clean(ids: readonly string[] | undefined): string[] {
  const out: string[] = [];
  for (const raw of ids ?? []) {
    const id = String(raw).trim();
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}

export function normalizeScope(scope: Partial<PositionsScope> | null | undefined): PositionsScope {
  return {
    books: clean(scope?.books).sort(),
    entities: clean(scope?.entities).sort(),
    accounts: clean(scope?.accounts).sort(),
  };
}

export function isAllScope(scope: PositionsScope): boolean {
  return !scope.books.length && !scope.entities.length && !scope.accounts.length;
}

/** Stable identity for comparing scopes and keying queries. */
export function scopeKey(scope: PositionsScope): string {
  const s = normalizeScope(scope);
  return `b=${s.books.join(",")};e=${s.entities.join(",")};a=${s.accounts.join(",")}`;
}

export function sameScope(a: PositionsScope, b: PositionsScope): boolean {
  return scopeKey(a) === scopeKey(b);
}

/** Query params for /positions/snapshot and /positions/summary (empty for "all"). */
export function positionsScopeQueryParams(scope: PositionsScope): Record<string, string> {
  const out: Record<string, string> = {};
  if (scope.books.length) out.books = scope.books.join(",");
  if (scope.entities.length) out.entity = scope.entities.join(",");
  if (scope.accounts.length) out.account = scope.accounts.join(",");
  return out;
}

/** Same rule as the server: every non-empty axis must match (case-insensitive). */
export function scopeAdmits(
  scope: PositionsScope,
  row: { book_id?: unknown; entity_id?: unknown; account_id?: unknown },
): boolean {
  const axis = (ids: string[], value: unknown) =>
    ids.length === 0 ||
    (typeof value === "string" && ids.some((id) => id.toLowerCase() === value.toLowerCase()));
  return (
    axis(scope.books, row.book_id) &&
    axis(scope.entities, row.entity_id) &&
    axis(scope.accounts, row.account_id)
  );
}

export function describeScope(scope: PositionsScope): string {
  if (isAllScope(scope)) return "All books";
  const parts: string[] = [];
  if (scope.books.length === 1) parts.push(scope.books[0]);
  else if (scope.books.length > 1) parts.push(`${scope.books.length} books`);
  if (scope.entities.length) parts.push(scope.entities.length === 1 ? scope.entities[0] : `${scope.entities.length} entities`);
  if (scope.accounts.length) parts.push(scope.accounts.length === 1 ? scope.accounts[0] : `${scope.accounts.length} accounts`);
  return parts.join(" / ");
}

function readJson<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage blocked: recent list and default cache are conveniences only */
  }
}

interface PositionsScopeState {
  /** Scope the dashboard is showing now. */
  scope: PositionsScope;
  /** Server-side default; null when none is saved (or not loaded yet). */
  defaultScope: PositionsScope | null;
  /** True once the server default was read (or failed), or the user picked a scope. */
  ready: boolean;
  /** The user changed scope in this session; a late default must not override it. */
  userPicked: boolean;
  recent: PositionsScope[];
  /** User picks a scope from the selector. */
  selectScope: (scope: PositionsScope) => void;
  /** Server default arrived (GET) or was saved / reset. */
  applyServerDefault: (scope: PositionsScope | null, opts?: { force?: boolean }) => void;
  /** The default could not be read: open on what we have. */
  markReady: () => void;
}

function cachedDefault(): PositionsScope | null {
  const cached = readJson<PositionsScope>(DEFAULT_CACHE_KEY);
  return cached ? normalizeScope(cached) : null;
}

function initialRecent(): PositionsScope[] {
  const list = readJson<PositionsScope[]>(RECENT_KEY);
  return Array.isArray(list) ? list.map(normalizeScope).filter((s) => !isAllScope(s)).slice(0, MAX_RECENT) : [];
}

const seed = cachedDefault();

export const usePositionsScopeStore = create<PositionsScopeState>((set, get) => ({
  // Open on the last default this browser saw so the first request is already
  // scoped; the server value reconciles it a moment later.
  scope: seed ?? ALL_POSITIONS_SCOPE,
  defaultScope: seed,
  ready: seed !== null,
  userPicked: false,
  recent: initialRecent(),
  selectScope: (next) => {
    const scope = normalizeScope(next);
    let recent = get().recent;
    if (!isAllScope(scope)) {
      recent = [scope, ...recent.filter((r) => !sameScope(r, scope))].slice(0, MAX_RECENT);
      writeJson(RECENT_KEY, recent);
    }
    set({ scope, userPicked: true, ready: true, recent });
  },
  applyServerDefault: (raw, opts) => {
    const defaultScope = raw ? normalizeScope(raw) : null;
    writeJson(DEFAULT_CACHE_KEY, defaultScope);
    const { userPicked } = get();
    if (opts?.force || !userPicked) {
      set({ defaultScope, ready: true, scope: defaultScope ?? ALL_POSITIONS_SCOPE });
    } else {
      set({ defaultScope, ready: true });
    }
  },
  markReady: () => set({ ready: true }),
}));
